import React from "react";
import { Button, Col, Input, Spin, Table, Typography } from "antd";
import { EyeOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import useProgramStudentsPaymentSummary, {
  StudentPaymentSummaryRow,
} from "../../hooks/payments/useProgramStudentsPaymentSummary";
import {
  AcademyStatusBadge,
  ProgramStudentStatusBadge,
} from "../../../../shared/components/ui/AcademyStatusBadge";

const { Title, Text } = Typography;

const normalizeSearchText = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const TEACHER_PAYMENT_STATUS = {
  paid: { label: "Pagó", color: "#4bd542" },
  unpaid: { label: "No pagó", color: "#ff4d4f" },
} as const;

interface TableStudentPaymentSummaryProps {
  program_id: number;
}

export const TableStudentPaymentSummary = ({
  program_id,
}: TableStudentPaymentSummaryProps) => {
  const navigate = useNavigate();
  const { loading, rows, refresh } =
    useProgramStudentsPaymentSummary(program_id);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleViewStudent = (userId: number) => {
    navigate(
      `/gestion-academy/personal-info/${userId}/${program_id}?tab=4`
    );
  };

  const filteredRows = React.useMemo(() => {
    const query = normalizeSearchText(searchQuery);
    if (!query) return rows;
    return rows.filter((item) => {
      const name = normalizeSearchText(
        `${item.user?.first_name ?? ""} ${item.user?.last_name ?? ""}`
      );
      const code = normalizeSearchText(item.user?.student_code ?? "");
      return name.includes(query) || code.includes(query);
    });
  }, [rows, searchQuery]);

  const columns: ColumnsType<StudentPaymentSummaryRow> = [
    {
      title: "Código",
      key: "student_code",
      render: (_, item) => (
        <Title style={{ fontSize: "0.74rem", fontWeight: "500" }} level={5}>
          {item?.user?.student_code ?? "—"}
        </Title>
      ),
    },
    {
      title: "Estudiante",
      key: "name",
      render: (_, item) => (
        <Col style={{ display: "flex", width: "100%", flexDirection: "row" }}>
          <Title style={{ fontSize: "0.74rem", fontWeight: "500" }} level={5}>
            {`${item?.user?.first_name ?? ""} ${item?.user?.last_name ?? ""}`.trim() ||
              "—"}
          </Title>
        </Col>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (_, item) => item?.user?.email ?? "—",
    },
    {
      title: "Fecha de matrícula",
      key: "enrollment_date",
      render: (_, item) =>
        item?.enrollment_date
          ? moment(item.enrollment_date).format("DD/MM/YYYY")
          : "—",
    },
    {
      title: "Estado inscripción",
      key: "status",
      render: (_, item) => <ProgramStudentStatusBadge status={item.status} />,
    },
    {
      title: "Estado de pago",
      key: "payment_status",
      render: (_, item) => {
        const config = TEACHER_PAYMENT_STATUS[item.paymentStatus];
        return (
          <AcademyStatusBadge
            background={config.color}
            label={config.label}
            variant="compact"
          />
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, item) => (
        <Col
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
          }}
        >
          <Button
            type="primary"
            shape="circle"
            icon={<EyeOutlined />}
            size="large"
            onClick={() => handleViewStudent(item.user_id)}
          />
        </Col>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
        <Input
          placeholder="Buscar por nombre o código de estudiante"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 320 }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Button icon={<ReloadOutlined />} onClick={() => refresh()}>
          Actualizar
        </Button>
      </div>
      {searchQuery.trim() && (
        <Text className="block mb-3 text-sm text-gray-500">
          Mostrando {filteredRows.length} de {rows.length} estudiantes
        </Text>
      )}
      <Table<StudentPaymentSummaryRow>
        rowKey="id"
        columns={columns}
        dataSource={filteredRows}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["15", "20", "50", "100"],
          showTotal: (total) => `${total} estudiantes`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
        scroll={{ x: true }}
        locale={{
          emptyText: searchQuery.trim()
            ? "No se encontraron estudiantes con ese criterio."
            : "No hay estudiantes inscritos en este programa.",
        }}
      />
    </div>
  );
};
